import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { saveAs } from 'file-saver';

// ------ Services ------ //
import * as webBridgeService from '../../../services/webBridgeService'
import * as exhibitorLeadCaptureService from '../../../services/exhibitorLeadCaptureService'
// ------ Styles ------ //
import styles from './ExhibitorLeads.module.css'
// ------ Components ------ //
import SearchBar from "../../../components/Miscellaneous/SearchBar"
import LoadingSpinner from '../../../components/Miscellaneous/LoadingSpinner'
import HoverButton from '../../../components/Miscellaneous/HoverButton'
import LeadItem from '../../../components/LeadCapture/ExhibitorLeads/LeadItem'
// ------ Font Awesome assets ------ //
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquareCaretUp, faSquareCaretDown } from '@fortawesome/free-solid-svg-icons'
// ------ Image Elements ------ //
const up = <FontAwesomeIcon icon={faSquareCaretUp} style={{ color: 'blue', width: '20px', height: '20px' }} />
const down = <FontAwesomeIcon icon={faSquareCaretDown} style={{ color: 'blue', width: '20px', height: '20px' }} />

const ExhibitorLeads = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const exhibitorAccount = location.state
    const [isCleared, setIsCleared] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [message, setMessage] = useState("")
    const [messageColor, setMessageColor] = useState('green')
    const [leads, setLeads] = useState([])
    const [leadsFile, setLeadsFile] = useState([])
    const [processing, setProcessing] = useState(false)
    const [leadScannerIdFilter, setLeadScannerIdFilter] = useState("")
    const [leadNameFilter, setLeadNameFilter] = useState("")
    const [leadOrganizationFilter, setLeadOrganizationFilter] = useState("")
    const [leadEmailFilter, setLeadEmailFilter] = useState("")
    const [leadLocationFilter, setLeadLocationFilter] = useState("")
    const [combinedFilteredLeads, setCombinedFilteredLeads] = useState([])
    const [email, setEmail] = useState(null)
    const [exhibitor, setExhibitor] = useState(null)
    const [sorting, setSorting] = useState({ key: "lastName", ascending: true });
    const [exhibitorLeadCaptureInfo, setExhibitorLeadCaptureInfo] = useState({});
    const exhibitorLeadCaptureInfoRef = useRef(null);
    if (exhibitorLeadCaptureInfoRef === null || exhibitorLeadCaptureInfoRef.current === null) {
        if (!exhibitorAccount || !exhibitorAccount.clientName) {
            exhibitorLeadCaptureInfoRef.current = exhibitorLeadCaptureService.getExhibitorLeadCapture();
        }
        else {
            let passedData = {
                clientId: exhibitorAccount.clientId,
                clientName: exhibitorAccount.clientName,
                leadCaptureId: exhibitorAccount.leadCaptureId,
                leadCaptureName: exhibitorAccount.leadCaptureName,
                exhibitorId: exhibitorAccount.exhibitorId,
            }
            exhibitorLeadCaptureInfoRef.current = passedData
        }
    }
    function applySorting(key) {
        if (sorting.key === key) {
            setSorting({ ...sorting, ascending: !sorting.ascending });
        } else {
            setSorting({ key: key, ascending: true });
        }
    }

    useEffect(() => {
        const interval = setInterval(() => checkExhibitorLeadCapture(), (300)) // run events fetch every .3 seconds
        return () => clearInterval(interval)
    }, [])
    const checkExhibitorLeadCapture = () => {
        // Update clientevent with every render when its value has changed.
        let savedExhibitorLeadCapture = exhibitorLeadCaptureService.getExhibitorLeadCapture();
        if (exhibitorLeadCaptureInfoRef.current && exhibitorLeadCaptureInfoRef.current.leadCaptureId !== undefined &&
            savedExhibitorLeadCapture && savedExhibitorLeadCapture.leadCaptureId !== undefined && savedExhibitorLeadCapture.exhibitorId !== undefined &&
            (savedExhibitorLeadCapture.leadCaptureId !== exhibitorLeadCaptureInfoRef.current.leadCaptureId ||
                savedExhibitorLeadCapture.exhibitorId !== exhibitorLeadCaptureInfoRef.current.exhibitorId)) {
            setExhibitorLeadCaptureInfo(savedExhibitorLeadCapture)
            exhibitorLeadCaptureInfoRef.current = savedExhibitorLeadCapture;
        }
    }
    // ------ Name Search ------ //
    const NameFilter = filterString => {
        if (filterString && filterString.toLowerCase) {
            filterString = filterString.toLowerCase()
        }
        let filtered = []
        leads.forEach((lead) => {
            const nameFirst = lead.firstName.toLowerCase()
            const nameLast = lead.lastName.toLowerCase()
            if (!filterString || nameFirst.includes(filterString) || nameLast.includes(filterString)) {
                filtered.push(lead)
            }
        })
        return filtered
    }

    const filteredByName = leads && leads.length ? NameFilter(leadNameFilter) : []

    // ------ ScannerId Search ------ //
    const ScannerIdFilter = filterString => {
        if (filterString && filterString.toLowerCase) {
            filterString = filterString.toLowerCase()
        }
        let filtered = []
        leads.forEach((lead) => {
            const scannerId = lead.scannerId ? lead.scannerId : ''
            if (!filterString || scannerId.toString().includes(filterString)) {
                filtered.push(lead)
            }
        })
        return filtered
    }

    const filteredByScannerId = leads && leads.length ? ScannerIdFilter(leadScannerIdFilter) : []

    // ------ Organization Search ------ //
    const OrganizationFilter = filterString => {
        if (filterString && filterString.toLowerCase) {
            filterString = filterString.toLowerCase()
        }
        let filtered = []
        leads.forEach((lead) => {
            const organization = lead.organization ? lead.organization.toLowerCase() : ''
            if (!filterString || organization.includes(filterString)) {
                filtered.push(lead)
            }
        })
        return filtered
    }

    const filteredByOrganization = leads && leads.length ? OrganizationFilter(leadOrganizationFilter) : []

    // ------ Email Search ------ //
    const EmailFilter = filterString => {
        if (filterString && filterString.toLowerCase) {
            filterString = filterString.toLowerCase()
        }
        let filtered = []
        leads.forEach((lead) => {
            const email = lead.emailAddress ? lead.emailAddress.toLowerCase() : ''
            if (!filterString || email.includes(filterString)) {
                filtered.push(lead)
            }
        })
        return filtered
    }

    const filteredByEmail = leads && leads.length ? EmailFilter(leadEmailFilter) : []

    // ------ Location Search ------ //
    const LocationFilter = filterString => {
        if (filterString && filterString.toLowerCase) {
            filterString = filterString.toLowerCase()
        }
        let filtered = []
        leads.forEach((lead) => {
            const locationCity = lead.city.toLowerCase()
            const locationState = lead.state.toLowerCase()
            const locationCountry = lead.country.toLowerCase()
            if (!filterString || locationCity.includes(filterString) || locationState.includes(filterString) || locationCountry.includes(filterString)) {
                filtered.push(lead)
            }
        })
        return filtered
    }

    const filteredByLocation = leads && leads.length ? LocationFilter(leadLocationFilter) : []

    // ------ Helper Functions ------ //
    const clearFilters = () => {
        setIsCleared(true)
        setLeadNameFilter("")
        setLeadScannerIdFilter("")
        setLeadOrganizationFilter("")
        setLeadEmailFilter("")
        setLeadLocationFilter("")
        setIsCleared(false)
    }
    function applySorting(key) {
        if (sorting.key === key) {
            setSorting({ ...sorting, ascending: !sorting.ascending });
        } else {
            setSorting({ key: key, ascending: true });
        }
    }

    // ------ useEffect ------ //
    useEffect(() => {
        if (leads && leads.length > 0) {
            let leds = [...leads];
            let filteredResults = []
            // apply filtering
            leds.forEach((led) => {
                if (filteredByName.includes(led)) {
                    if (filteredByScannerId.includes(led)) {
                        if (filteredByOrganization.includes(led)) {
                            if (filteredByEmail.includes(led)) {
                                if (filteredByLocation.includes(led)) {
                                    filteredResults.push(led)
                                }
                            }
                        }
                    }
                }
            })
            // apply sorting
            let sortedResults = filteredResults.sort((a, b) => {
                const optionA = a[sorting.key] && a[sorting.key] !== undefined ? a[sorting.key].toString().toLowerCase() : ''

                const optionB = b[sorting.key] && b[sorting.key] !== undefined ? b[sorting.key].toString().toLowerCase() : ''

                if (optionA < optionB) {
                    return sorting.ascending ? -1 : 1;
                }
                if (optionA > optionB) {
                    return sorting.ascending ? 1 : -1;
                }
                return 0;
            })
            setCombinedFilteredLeads(sortedResults);
        }

    }, [leads, leadNameFilter, leadScannerIdFilter, leadOrganizationFilter, leadEmailFilter, leadLocationFilter, sorting])
    const isEmailInvalid = () => {
        if (!email) return false; // empty field is valid for local download
        const validEmailRegex = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
        const emailParts = email.split('@');

        if (emailParts.length !== 2) return true;
        const account = emailParts[0];
        const domain = emailParts[1];

        if (account.length > 64) return true;
        else if (domain.length > 255) return true;

        const domainParts = domain.split('.');

        if (domainParts.some((part) => part.length > 63)) return true;

        return !(validEmailRegex.test(email));
    }
    const handleChange = e => {
        setEmail(e.target.value);
    }
    const createRequestBody = async () => {

        let requestBody = {}
        requestBody =
        {
            "emailAddress": `${email}`,
            "exhibitorId": `${exhibitor.id}`,
            
        }
        return await requestBody;
    }
    const generateLeadsFile = async () => {
        try {
            setMessage('Downloading...')
            setMessageColor('green')
            let leadsFile = await webBridgeService.generateExhibitorLeads(exhibitor.id)
            if (leadsFile) {
                setLeadsFile(leadsFile);
                setMessage('Download Complete!')
                setMessageColor('green')
            }
            return leadsFile;

        } catch (err) {
            setMessage('Error: ' + err.message);
            setMessageColor('red')
        }
    }
    const generateEmail = async () => {
        const getReqBody = await createRequestBody()
        try {
            await webBridgeService.emailExhibitorLeads(getReqBody)
            setMessage('Email sent!')
            setMessageColor('green')
        } catch (err) {
            setMessage('Error: ' + err.message);
            setMessageColor('red')
        }
    }
    function base64ToBlob(base64Data, contentType = 'application/vnd.ms-excel') {
        let byteCharacters = '';
        if (base64Data.split(',').length > 1) {
            byteCharacters = atob(base64Data.split(',')[1]);
        }
        else {
            byteCharacters = atob(base64Data);
        }
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays, { type: contentType });
    }
    const fetchLeads = async (id) => {
        let leadsList = await webBridgeService.getExhibitorLeads(id);
        if (leadsList && leadsList.length > 0) {
            setLeads(leadsList);
        }
        setLoaded(true);
    };

    const handleJSDownload = async (e) => {
        e.preventDefault();
        setProcessing(true)
        if (email && !isEmailInvalid()) {
            setMessage('Sending...')
            setMessageColor('green')
            // send email
            await generateEmail();
            setProcessing(false)
            return;
        }
        let holder = null;
        if (leadsFile || leadsFile.length < 1) {
            setMessage('Downloading...')
            setMessageColor('green')
            holder = await generateLeadsFile();
            setProcessing(false)
        }
        if ((leadsFile && leadsFile.attachment) || (holder && holder.attachment)) {
            let buffer = leadsFile && leadsFile.length > 0 ? base64ToBlob(leadsFile.attachmentBase64) : base64ToBlob(holder.attachmentBase64);
            // Save the Excel file
            saveAs(new Blob([buffer]), `${exhibitor.organization}.xlsx`);
        }

    };
    useEffect(() => {
        setProcessing(false);
        if (exhibitor && exhibitor.id) {
            fetchLeads(exhibitor.id);
        }

    }, [exhibitor])
    
    useEffect(() => {
        // get exhibitor
        if (exhibitorAccount && exhibitorAccount.id) {
            setExhibitor(exhibitorAccount);
        }
        if (exhibitorLeadCaptureInfo && exhibitorLeadCaptureInfo.exhibitorId) {
            exhibitorLeadCaptureInfo.id = exhibitorLeadCaptureInfo.exhibitorId;
            setExhibitor(exhibitorLeadCaptureInfo);
        }
    }, [exhibitorLeadCaptureInfo])
    useEffect(() => {
        // check if entry is stored
        // store into local object
        let localExhibitorLeadCapture = exhibitorLeadCaptureService.getExhibitorLeadCapture()
        if (localExhibitorLeadCapture) {
            setExhibitorLeadCaptureInfo(localExhibitorLeadCapture)
        }
        if (!localExhibitorLeadCapture) {
            setLoaded(true);
        }
        setProcessing(false)
    }, [])
    return (
        <div id={styles.eventConfigurePage}>
            <h1>Exhibitor Leads - </h1><br />
            {exhibitor && exhibitor.id ?
                <>
                    <h1>{exhibitor.leadCaptureName}</h1>
                    {exhibitor.firstname ? <h2>Mobile App lead(s) for {exhibitor.firstName} {exhibitor.lastName} - {exhibitor.organization} ({exhibitor.refNumber}).</h2> :
                        <></>}
                    
                </>
                : <></>}
            <br /><br />
            <>
                {
                    !loaded ? <LoadingSpinner /> :
                        <>
                        <br/>
                            <SearchBar
                                searchName={leadScannerIdFilter}
                                handleSearchName={setLeadScannerIdFilter}
                                placeholder="Lead id"
                                label="ScannerId"
                                isCleared={isCleared === true || isCleared === false ? isCleared : false}
                            />
                            <SearchBar
                                searchName={leadNameFilter}
                                handleSearchName={setLeadNameFilter}
                                placeholder="Lead First/Last Name"
                                label="Name"
                                isCleared={isCleared === true || isCleared === false ? isCleared : false}
                            />
                            <SearchBar
                                searchName={leadOrganizationFilter}
                                handleSearchName={setLeadOrganizationFilter}
                                placeholder="Lead Organization"
                                label="Organization"
                                isCleared={isCleared === true || isCleared === false ? isCleared : false}
                            />
                            <SearchBar
                                searchName={leadEmailFilter}
                                handleSearchName={setLeadEmailFilter}
                                placeholder="Lead Email"
                                label="Email"
                                isCleared={isCleared === true || isCleared === false ? isCleared : false}
                            />
                            <SearchBar
                                searchName={leadLocationFilter}
                                handleSearchName={setLeadLocationFilter}
                                placeholder="Lead City/State/Country"
                                label="Location"
                                isCleared={isCleared === true || isCleared === false ? isCleared : false}
                            />
                            {leadScannerIdFilter || leadNameFilter || leadOrganizationFilter || leadEmailFilter || leadLocationFilter ?
                                <button onClick={clearFilters} id={styles.clearFilters}>
                                    Clear
                                </button>
                                :
                                <>
                                </>
                            }
                            <br /> <br />
                            <label> <strong>Enter Email address or leave blank to download leads locally.</strong> </label>
                            <input
                                type='email'
                                value={email}
                                onChange={handleChange} // option selected
                                name='email'
                                placeholder='Enter Email...'
                            />
                            <br />
                            <button onClick={e => handleJSDownload(e)} disabled={processing} >
                                {email && !isEmailInvalid() ? 'Email Leads' : 'Download Leads'}
                            </button>
                            {
                                message ?
                                    <h2 style={{ color: messageColor }}>{message}</h2> :
                                    <></>
                            }
                            <br />
                            <h3>Total Leads: {leads.length}</h3>
                            <br />
                            <br />
                            {
                                combinedFilteredLeads && combinedFilteredLeads.length > 0 ?
                                    <>
                                        <table className={styles.tableLeads}>
                                            <thead>
                                                <tr className={styles.trLeads}>
                                                    <th className={styles.thLeads} onClick={() => applySorting('scannerField')}>
                                                        ScannerId {sorting.key === 'scannerField' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Registrant id'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads} onClick={() => applySorting('lastName')}>
                                                        Name {sorting.key === 'lastName' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Lead name'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads} onClick={() => applySorting('organization')}>
                                                        Organization {sorting.key === 'organization' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Lead organization'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads} onClick={() => applySorting('emailAddress')}>
                                                        Email {sorting.key === 'emailAddress' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Lead email'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads} onClick={() => applySorting('city')}>
                                                        City {sorting.key === 'city' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Lead city'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads} onClick={() => applySorting('state')}>
                                                        State {sorting.key === 'state' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Lead state'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads} onClick={() => applySorting('country')}>
                                                        Country {sorting.key === 'country' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Lead country'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads} onClick={() => applySorting('scanDate')}>
                                                        Scan Date {sorting.key === 'refNumber' && sorting.ascending ? up : down}
                                                        <HoverButton
                                                            title={'Date of last scan'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads}>
                                                        Qualifiers
                                                        <HoverButton
                                                            title={'Lead qualifier selection'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                    <th className={styles.thLeads}>
                                                        Notes
                                                        <HoverButton
                                                            title={'Lead notes'}
                                                            messageWidth={75}
                                                        />
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody key={combinedFilteredLeads.length}>
                                                {combinedFilteredLeads.map((item, index) => (
                                                    <LeadItem
                                                        index={index}
                                                        lead={item}
                                                    >
                                                    </LeadItem>
                                                ))}
                                            </tbody>

                                        </table></>

                                    : <>
                                        <div>
                                            {leads && leads.length > 0 ?
                                                <div className={styles.errorLeadsMessage}> No leads match filter.</div>
                                                :
                                                <div className={styles.errorLeadsMessage}> No leads found!</div>
                                            }
                                        </div>
                                    </>

                            }

                        </>
                }
            </>
            <br/>
            <button id={styles.backButton} onClick={() => navigate(-1)}> Back </button>
        </div>
    );
}

export default ExhibitorLeads;